import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { can, allowedBranches } from '../lib/perms';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { ClipboardList, Check, CheckCircle2, Calendar, Truck, Edit, Trash2, Eye, ArrowLeft, FileClock, Save, Printer } from 'lucide-react';
import { EditRecordDialog } from '../components/EditRecordDialog';
import { ShareStockTakeLink } from '../components/ShareStockTakeLink';
import { printStockTakeSheet, printSavedStockTake, qzErrorMessage } from '../lib/qzPrint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const BRANCH_KEY = 'stocktake_branch';

const isEmpty = (v) => v === undefined || v === null || String(v).trim() === '';
const orderQty = (item, soh) => Math.max(0, Math.round(((item.par || 0) - soh) * 100) / 100);

const SupplierTile = ({ s, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`supplier-tile-${s.supplier_id}`}
    className={`text-left rounded-lg border p-4 transition-[transform,background-color,opacity] duration-150 hover:-translate-y-0.5 ${
      s.done ? 'bg-muted opacity-60 border-dashed' : 'bg-card hover:bg-accent/10 border-border'
    }`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-center gap-2">
        <Truck className={`w-5 h-5 ${s.done ? 'text-muted-foreground' : 'text-primary'}`} />
        <p className="font-semibold">{s.supplier_name}</p>
      </div>
      {s.done && (
        <Badge variant="secondary" className="gap-1" data-testid={`supplier-done-${s.supplier_id}`}>
          <CheckCircle2 className="w-3.5 h-3.5" />Done
        </Badge>
      )}
      {s.has_draft && (
        <Badge variant="outline" className="gap-1 border-amber-500 text-amber-700" data-testid={`supplier-draft-${s.supplier_id}`}>
          <FileClock className="w-3.5 h-3.5" />Draft
        </Badge>
      )}
    </div>
    <p className="text-sm text-muted-foreground mt-2">{s.item_count} {s.item_count === 1 ? 'item' : 'items'} to count</p>
    {s.has_draft && (
      <p className="text-xs text-amber-700 mt-1" data-testid={`supplier-draft-by-${s.supplier_id}`}>{s.draft_via_link ? 'Counted via link by' : 'Draft saved by'} {s.draft_by}{s.draft_at ? ` · ${new Date(s.draft_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}</p>
    )}
    {s.done && (
      <p className="text-xs text-muted-foreground mt-1">
        {s.order_name}{s.done_by ? ` · by ${s.done_by}` : ''}
      </p>
    )}
  </button>
);

const StockTake = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMasterAdmin = can(user, 'stock_take.manage');
  const canCount = can(user, 'stock_take.count');
  const canShare = ['master_admin', 'manager'].includes(user?.role);
  const [branches, setBranches] = useState([]);
  const [stockTakes, setStockTakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBranch, setSelectedBranch] = useState(localStorage.getItem(BRANCH_KEY) || user?.branch_id || '');
  const [selectedDay, setSelectedDay] = useState(new Date().toLocaleDateString('en-US', { weekday: 'long' }));
  const [dayData, setDayData] = useState(null);
  const [activeSupplier, setActiveSupplier] = useState(null);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [editingStockTake, setEditingStockTake] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchHistory = useCallback(async () => {
    const res = await axios.get(`${API}/stock-takes`, { withCredentials: true });
    setStockTakes(res.data);
  }, []);

  const fetchDay = useCallback(async () => {
    if (!selectedBranch) return;
    try {
      const res = await axios.get(`${API}/stock-takes/day-suppliers`, {
        withCredentials: true, params: { branch_id: selectedBranch, day_of_week: selectedDay },
      });
      setDayData(res.data);
    } catch { toast.error('Failed to load suppliers'); }
  }, [selectedBranch, selectedDay]);

  useEffect(() => {
    Promise.all([axios.get(`${API}/branches`, { withCredentials: true }), fetchHistory()])
      .then(([b]) => { const allowed = allowedBranches(user, b.data); setBranches(allowed); if (allowed.length === 1) setSelectedBranch(allowed[0].branch_id); else if (selectedBranch && !allowed.some((x) => x.branch_id === selectedBranch)) setSelectedBranch(''); })
      .catch(() => toast.error('Failed to load data'))
      .finally(() => setLoading(false));
  }, [fetchHistory]);

  useEffect(() => { fetchDay(); }, [fetchDay]);

  const chooseBranch = (v) => { setSelectedBranch(v); localStorage.setItem(BRANCH_KEY, v); setActiveSupplier(null); };

  const openSupplier = async (s) => {
    if (s.done) {
      if (s.order_id) navigate(`/orders?order=${s.order_id}`);
      else toast.info(`${s.supplier_name} is done for ${selectedDay} (nothing needed ordering)`);
      return;
    }
    try {
      const res = await axios.get(`${API}/stock-takes/today-items`, {
        withCredentials: true, params: { branch_id: selectedBranch, supplier_id: s.supplier_id },
      });
      const draft = await axios.get(`${API}/stock-takes/draft`, {
        withCredentials: true, params: { branch_id: selectedBranch, supplier_id: s.supplier_id, day_of_week: selectedDay },
      });
      const saved = draft.data.counts || {};
      setItems(res.data.items);
      setCounts(Object.fromEntries(Object.entries(saved).map(([k, v]) => [k, String(v)])));
      setShowErrors(false);
      setActiveSupplier(s);
      if (draft.data.updated_by) toast.info(`Continuing draft saved by ${draft.data.updated_by}`);
    } catch { toast.error('Failed to load items'); }
  };

  const handleSave = async () => {
    const missing = items.filter((i) => isEmpty(counts[i.stock_item_id]));
    if (missing.length) {
      setShowErrors(true);
      toast.error(`${missing.length} item${missing.length === 1 ? ' needs' : 's need'} a count before saving`);
      const el = document.querySelector(`[data-testid="soh-input-${missing[0].stock_item_id}"]`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
      return;
    }
    const rows = items.map((i) => ({ item_id: i.stock_item_id, soh: parseFloat(counts[i.stock_item_id]) }));
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/stock-takes`, {
        branch_id: selectedBranch, supplier_id: activeSupplier.supplier_id, day_of_week: selectedDay, items: rows,
      }, { withCredentials: true });
      setActiveSupplier(null);
      fetchHistory();
      if (res.data.order_id) {
        toast.success(`Purchase order ${res.data.order_name} created`);
        navigate(`/orders?order=${res.data.order_id}`);
      } else {
        toast.success('Stock take saved — nothing needs ordering');
        fetchDay();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save stock take');
    } finally { setSubmitting(false); }
  };

  const runPrint = async (fn) => {
    try {
      const p = await fn();
      toast.success(`Sent to ${p.name || p.ip_address}`);
    } catch (e) { toast.error(qzErrorMessage(e)); }
  };

  const printSheet = () => runPrint(() => printStockTakeSheet({
    branch_id: selectedBranch, supplier_id: activeSupplier.supplier_id, day_of_week: selectedDay, counts,
  }));

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const res = await axios.put(`${API}/stock-takes/draft`, {
        branch_id: selectedBranch, supplier_id: activeSupplier.supplier_id, day_of_week: selectedDay, counts,
      }, { withCredentials: true });
      toast.success(`Draft saved (${res.data.count} of ${items.length} counted)`);
      setActiveSupplier(null);
      fetchDay();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save draft');
    } finally { setSavingDraft(false); }
  };

  const handleSaveEdit = async (payload) => {
    setSavingEdit(true);
    try {
      await axios.put(`${API}/stock-takes/${editingStockTake.stock_take_id}`, payload, { withCredentials: true });
      toast.success('Stock take updated');
      setEditingStockTake(null);
      fetchHistory();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update stock take'); }
    finally { setSavingEdit(false); }
  };

  const openDelete = async (st) => {
    try {
      const res = await axios.get(`${API}/orders`, { withCredentials: true, params: { stock_take_id: st.stock_take_id } });
      setDeleteTarget({ st, orderCount: res.data.length });
    } catch { toast.error('Failed to check linked orders'); }
  };

  const handleDelete = async (deleteOrders) => {
    try {
      const res = await axios.delete(`${API}/stock-takes/${deleteTarget.st.stock_take_id}`,
        { withCredentials: true, params: { delete_orders: deleteOrders } });
      toast.success(res.data.deleted_orders ? `Stock take and ${res.data.deleted_orders} order(s) deleted` : 'Stock take deleted');
      setDeleteTarget(null);
      fetchHistory();
      fetchDay();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete stock take'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const suppliers = dayData?.suppliers || [];
  const doneCount = suppliers.filter((s) => s.done).length;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="stocktake-page">
      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>Stock Take</h1>
        <p className="text-muted-foreground mt-1">
          <Calendar className="w-4 h-4 inline mr-1" />
          {selectedDay}{dayData?.date ? ` ${new Date(dayData.date).toLocaleDateString()}` : ''} — count each supplier, then save to create its order
        </p>
      </div>

      <Tabs defaultValue="new" className="space-y-6">
        <TabsList>
          <TabsTrigger value="new" data-testid="tab-new-stocktake">Stock Take</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-stocktake-history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="new" className="space-y-6">
          <Card>
            <CardContent className="pt-6 flex flex-col md:flex-row gap-6">
              <div className="space-y-2 md:w-64">
                <Label>Branch</Label>
                <Select value={selectedBranch} onValueChange={chooseBranch}>
                  <SelectTrigger data-testid="branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Order Day</Label>
                <div className="flex gap-1 flex-wrap">
                  {DAYS.map((day) => (
                    <Button key={day} size="sm" variant={selectedDay === day ? 'default' : 'outline'}
                      onClick={() => { setSelectedDay(day); setActiveSupplier(null); }}
                      data-testid={`day-btn-${day.toLowerCase()}`} className="px-3 text-xs">
                      {day.slice(0, 3)}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {!selectedBranch && <p className="text-muted-foreground">Select a branch to start.</p>}

          {selectedBranch && !activeSupplier && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground" data-testid="day-progress">
                {suppliers.length === 0 ? `No suppliers with items for this branch order on ${selectedDay}.` : `${doneCount} of ${suppliers.length} suppliers done for ${selectedDay}`}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="supplier-tiles">
                {suppliers.map((s) => <SupplierTile key={s.supplier_id} s={s} onClick={() => openSupplier(s)} />)}
              </div>
            </div>
          )}

          {activeSupplier && (
            <Card data-testid="count-card">
              <CardHeader className="flex flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="icon" onClick={() => setActiveSupplier(null)} data-testid="back-to-suppliers-btn">
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <CardTitle className="text-lg">{activeSupplier.supplier_name}</CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  {canShare && items.length > 0 && <ShareStockTakeLink branchId={selectedBranch} supplier={activeSupplier} day={selectedDay} />}
                  <Badge variant="outline">{items.length} items</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {items.length === 0 ? (
                  <p className="text-muted-foreground">No items for this supplier have a PAR at this branch.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead className="text-center">PAR</TableHead>
                        <TableHead className="text-center">Stock on Hand</TableHead>
                        <TableHead className="text-center">Order</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item) => {
                        const raw = counts[item.stock_item_id];
                        const counted = !isEmpty(raw);
                        const invalid = showErrors && !counted;
                        const qty = orderQty(item, parseFloat(raw) || 0);
                        return (
                          <TableRow key={item.stock_item_id} className={invalid ? 'bg-red-50' : ''}>
                            <TableCell>
                              <p className="font-medium">{item.name}</p>
                              {item.notes && <p className="text-xs text-muted-foreground italic">{item.notes}</p>}
                            </TableCell>
                            <TableCell>{item.unit}</TableCell>
                            <TableCell className="text-center font-medium">{item.par}</TableCell>
                            <TableCell className="text-center">
                              <Input type="number" inputMode="decimal" step="0.01" min="0" value={raw ?? ''}
                                onChange={(e) => setCounts((c) => ({ ...c, [item.stock_item_id]: e.target.value }))}
                                required aria-invalid={invalid}
                                className={`w-24 mx-auto text-center ${invalid ? 'border-red-500 ring-2 ring-red-300 focus-visible:ring-red-400' : ''}`}
                                data-testid={`soh-input-${item.stock_item_id}`} />
                              {invalid && <p className="text-[11px] text-red-600 mt-1" data-testid={`soh-required-${item.stock_item_id}`}>Required</p>}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant={qty > 0 ? 'default' : 'secondary'} data-testid={`order-qty-${item.stock_item_id}`}>
                                {counted ? qty : '-'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
                <div className="flex justify-end gap-2">
                  <span className="text-sm text-muted-foreground mr-auto self-center" data-testid="count-progress">
                    {items.filter((i) => !isEmpty(counts[i.stock_item_id])).length} of {items.length} counted
                  </span>
                  <Button variant="outline" onClick={() => setActiveSupplier(null)}>Cancel</Button>
                  <Button variant="outline" onClick={printSheet} disabled={items.length === 0} data-testid="print-stocktake-sheet-btn">
                    <Printer className="w-4 h-4 mr-2" />Print
                  </Button>
                  <Button variant="outline" onClick={handleSaveDraft} disabled={!canCount || savingDraft || items.length === 0} data-testid="save-draft-btn">
                    <Save className="w-4 h-4 mr-2" />{savingDraft ? 'Saving...' : 'Save Draft'}
                  </Button>
                  <Button onClick={handleSave} disabled={!canCount || submitting || items.length === 0} data-testid="submit-stocktake-btn">
                    <Check className="w-4 h-4 mr-2" />{submitting ? 'Saving...' : 'Save & Create Order'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader><CardTitle>Stock Take History</CardTitle></CardHeader>
            <CardContent>
              {stockTakes.length === 0 ? (
                <div className="text-center py-8">
                  <ClipboardList className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No stock takes recorded yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>PO Number</TableHead>
                      <TableHead>Branch</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Counted By</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stockTakes.map((st) => (
                      <TableRow key={st.stock_take_id} data-testid={`stocktake-row-${st.stock_take_id}`}>
                        <TableCell>
                          {new Date(st.date).toLocaleDateString()}
                          {st.day_of_week && <p className="text-xs text-muted-foreground">for {st.day_of_week}</p>}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{st.order_name || '—'}</TableCell>
                        <TableCell>{st.branch_name}</TableCell>
                        <TableCell>{st.supplier_name || '—'}</TableCell>
                        <TableCell>{st.items?.length || 0}</TableCell>
                        <TableCell>
                          {st.counted_by || st.created_by}
                          {st.counted_by && st.counted_by !== st.created_by && (
                            <p className="text-xs text-muted-foreground" data-testid={`stocktake-submitted-by-${st.stock_take_id}`}>Submitted by {st.created_by}</p>
                          )}
                          {st.edited_at && (
                            <p className="text-xs text-muted-foreground" data-testid={`stocktake-edited-${st.stock_take_id}`}>
                              Edited by {st.edited_by} on {new Date(st.edited_at).toLocaleString()}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" title="Print stock take" onClick={() => runPrint(() => printSavedStockTake(st.stock_take_id))}
                              data-testid={`print-stocktake-${st.stock_take_id}`}>
                              <Printer className="w-4 h-4" />
                            </Button>
                            {st.order_id && (
                              <Button size="sm" variant="ghost" onClick={() => navigate(`/orders?order=${st.order_id}`)}
                                data-testid={`view-stocktake-order-${st.stock_take_id}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                            {isMasterAdmin && (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => setEditingStockTake(st)}
                                  data-testid={`edit-stocktake-${st.stock_take_id}`}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => openDelete(st)}
                                  data-testid={`delete-stocktake-${st.stock_take_id}`}>
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <EditRecordDialog open={!!editingStockTake} onOpenChange={(o) => !o && setEditingStockTake(null)}
        record={editingStockTake} mode="stocktake" onSave={handleSaveEdit} saving={savingEdit} />

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-stocktake-dialog">
          <DialogHeader>
            <DialogTitle>Delete stock take?</DialogTitle>
            <DialogDescription>
              {deleteTarget?.st.supplier_name} — {deleteTarget?.st.branch_name}. This cannot be undone.
              {deleteTarget?.orderCount > 0 && ` This stock take has ${deleteTarget.orderCount} linked order(s).`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="cancel-delete-stocktake-btn">Cancel</Button>
            {deleteTarget?.orderCount > 0 ? (
              <>
                <Button variant="secondary" onClick={() => handleDelete(false)} data-testid="delete-stocktake-keep-orders-btn">Delete, keep orders</Button>
                <Button variant="destructive" onClick={() => handleDelete(true)} data-testid="delete-stocktake-and-orders-btn">Delete with orders</Button>
              </>
            ) : (
              <Button variant="destructive" onClick={() => handleDelete(false)} data-testid="confirm-delete-stocktake-btn">Delete</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockTake;
